import React from "react";
import { Link } from "react-router-dom";
import LegalPage, {
	Bullets,
	Note,
	Section,
	SubSection,
} from "../components/legal/LegalPage";

/**
 * Public terms and conditions, from the client's approved copy. Apple asks for
 * an EULA or terms link on the store listing, and the app links here from
 * registration (#157).
 */
const Terms = () => (
	<LegalPage
		title="Terms and Conditions"
		updated="3 September 2026"
		toc
		intro={
			<>
				<p className="mb-3">Welcome to WholesaleDai.</p>
				<p className="mb-3">
					These Terms and Conditions ("Terms") govern your access to and use of
					the WholesaleDai website, mobile application and related services
					(together, the <span className="font-semibold">Platform</span>).
					Please read them carefully before using the Platform.
				</p>
				<p>
					By accessing, registering for or using WholesaleDai, you agree to be
					legally bound by these Terms. If you do not agree with them, you must
					not use the Platform.
				</p>
			</>
		}
	>
		<Section heading="1. Platform overview">
			<SubSection heading="1.1 B2B marketplace">
				<p>
					WholesaleDai operates an online business-to-business (B2B) marketplace
					that connects wholesalers, sellers, suppliers, retailers and other
					business buyers. The Platform facilitates commercial transactions
					involving wholesale and bulk purchases.
				</p>
			</SubSection>

			<SubSection heading="1.2 Business use only">
				<p>
					WholesaleDai is intended solely for legitimate commercial and business
					purposes. The Platform is not intended for personal consumer purchases
					unless we expressly permit it.
				</p>
			</SubSection>

			<SubSection heading="1.3 Marketplace facilitator">
				<p>
					WholesaleDai acts primarily as a technology and marketplace
					facilitator connecting Buyers and Sellers. Unless we expressly state
					otherwise, WholesaleDai is not the manufacturer, owner or direct
					seller of products listed by third-party Sellers.
				</p>
			</SubSection>

			<SubSection heading="1.4 Platform operator details">
				<p className="mb-3">
					As required by the Electronic Commerce Act, 2081 (2025), the details
					of the business operating this Platform are:
				</p>
				<Bullets
					items={[
						"Platform name: WholesaleDai",
						"Nature of business: intermediary B2B marketplace; WholesaleDai may also sell directly, and where it does so this is identified on the listing",
						"Office address: O2, Newroad, Kathmandu-22, Nepal",
						"Phone: +977-1-5927066",
						"Email: info@wholesaledai.com",
						"Website: www.wholesaledai.com",
					]}
				/>
			</SubSection>
		</Section>

		<Section heading="2. Eligibility">
			<p className="mb-3">To use the Platform, you must:</p>
			<Bullets
				items={[
					"Be at least 18 years of age, the age of majority under the National Civil Code, 2074 (2017)",
					"Have the legal capacity to enter into binding agreements",
					"Have the legal authority to represent and bind any business entity you represent",
					"Provide accurate and complete information",
					"Hold valid business registration documents where required",
					"Hold a PAN, VAT registration, company registration, licences or other documents required under applicable law",
				]}
			/>
			<p className="mt-3">
				WholesaleDai reserves the right to determine eligibility for Platform
				access, and may reject, suspend, restrict or terminate access where
				eligibility requirements are not met.
			</p>
		</Section>

		<Section heading="3. Account registration">
			<SubSection heading="3.1 Account creation">
				<p className="mb-3">
					Most features of the Platform require an account. You may be asked to
					provide your name, phone number, email address, business information,
					business registration details, PAN or VAT information and other
					information required for verification.
				</p>
				<p>
					You agree that all information you provide during registration is
					accurate, complete and current, and that you will keep it up to date.
				</p>
			</SubSection>

			<SubSection heading="3.2 Account verification">
				<p className="mb-3">
					WholesaleDai reviews new accounts before granting access. We may
					request PAN certificates, VAT certificates, company registration
					certificates, business licences, identification documents,
					authorisation documents and other legally required documents.
				</p>
				<p className="mb-3">
					An account awaiting review cannot sign in, browse the catalogue or
					place orders. If we cannot verify a business, or the documents appear
					inaccurate, the account is rejected — a rejected account can sign in
					for the sole purpose of submitting corrected documents for a further
					review.
				</p>
				<p>
					Once an account is approved, its PAN number and verification documents
					are locked and can only be changed by our staff. We reserve the right
					to approve, reject, suspend or terminate accounts based on
					verification results.
				</p>
			</SubSection>

			<SubSection heading="3.3 Account security">
				<p className="mb-3">
					You are responsible for maintaining the confidentiality and security
					of your account credentials, including passwords, OTPs, login
					credentials and the devices used to access your account.
				</p>
				<p>
					You are responsible for activity conducted through your account, and
					must notify WholesaleDai immediately if you suspect unauthorised
					access or a security breach. Do not share, sell or transfer your
					account to anyone else.
				</p>
			</SubSection>

			<SubSection heading="3.4 Account suspension or termination">
				<p className="mb-3">
					WholesaleDai may suspend, restrict or terminate an account where a
					user:
				</p>
				<Bullets
					items={[
						"Provides false or fraudulent information",
						"Violates these Terms",
						"Engages in unlawful activity or misuses the Platform",
						"Attempts fraud or payment manipulation",
						"Submits invalid verification documents",
						"Creates security risks",
					]}
				/>
			</SubSection>
		</Section>

		<Section heading="4. Buyer and Seller obligations">
			<SubSection heading="4.1 Seller obligations">
				<p className="mb-3">
					Sellers list products under a written agreement with WholesaleDai and
					agree to the following.
				</p>
				<p className="mb-2 font-semibold">Listing accuracy</p>
				<p className="mb-3">
					Sellers must keep product listings accurate and up to date, including
					product names, descriptions, specifications, images, wholesale prices,
					bulk discounts, Minimum Order Quantities (MOQs), stock availability
					and product quality information.
				</p>
				<p className="mb-2 font-semibold">Product compliance</p>
				<p className="mb-3">
					Sellers represent and warrant that the products they list are lawful,
					are not counterfeit, do not infringe intellectual property rights,
					comply with applicable laws and regulations, and meet applicable
					quality and safety requirements.
				</p>
				<p className="mb-2 font-semibold">Order fulfilment</p>
				<p className="mb-3">
					Sellers agree to fulfil accepted orders at the agreed prices,
					specifications, quantities, delivery terms and other agreed commercial
					conditions, and to make reasonable efforts to fulfil orders accurately
					and within agreed timelines.
				</p>
				<p className="mb-2 font-semibold">Tax invoices</p>
				<p>
					Sellers registered for VAT must issue a valid tax invoice for each
					sale as required by the Value Added Tax Act, 2052 (1996).
				</p>
			</SubSection>

			<SubSection heading="4.2 Buyer obligations">
				<p className="mb-3">
					Buyers agree to use the Platform responsibly and for legitimate
					business purposes.
				</p>
				<p className="mb-2 font-semibold">Order commitment</p>
				<p className="mb-3">
					Placing or confirming an order is a binding commercial commitment to
					purchase the specified products at the agreed quantity, wholesale
					price, trade terms and payment terms. Review order details carefully
					before confirming.
				</p>
				<p className="mb-2 font-semibold">Inspection of goods</p>
				<p>
					Buyers must inspect delivered goods promptly on receipt. Any
					discrepancy, damage, incorrect fulfilment or defect must be reported
					within the timeframe set out in section 11.
				</p>
			</SubSection>
		</Section>

		<Section heading="5. Product information">
			<p className="mb-3">
				Product information is provided by Sellers. WholesaleDai makes reasonable
				efforts to maintain an effective marketplace but does not guarantee that
				every product description, specification or image provided by a
				third-party Seller is completely accurate.
			</p>
			<p>
				Buyers are encouraged to verify important product information before
				placing an order.
			</p>
		</Section>

		<Section heading="6. Pricing">
			<SubSection heading="6.1 Wholesale pricing">
				<p className="mb-3">
					Wholesale prices, bulk discounts and quotations are displayed on the
					Platform or provided separately, and may depend on quantity, Minimum
					Order Quantity, Seller terms, product availability, applicable taxes
					and delivery arrangements.
				</p>
			</SubSection>

			<SubSection heading="6.2 Taxes">
				<p>
					Value Added Tax at the prevailing rate — currently 13% under the Value
					Added Tax Act, 2052 (1996) — and any other applicable taxes may be
					included in or added to product pricing. Each listing states whether
					the displayed price includes VAT. The applicable tax treatment depends
					on the transaction and the parties' registration status.
				</p>
			</SubSection>

			<SubSection heading="6.3 Price changes">
				<p>
					Prices may change without prior notice before an order is confirmed.
					Once an order has been accepted or confirmed, the agreed order terms
					govern the price, subject to correction of genuine errors, legal
					requirements or changes agreed between the parties.
				</p>
			</SubSection>
		</Section>

		<Section heading="7. Orders">
			<SubSection heading="7.1 Order placement">
				<p className="mb-3">
					Orders are placed through the Platform's ordering features. An order
					may be subject to Seller acceptance, product availability, payment
					verification, business verification and other applicable
					requirements.
				</p>
			</SubSection>

			<SubSection heading="7.2 Order acceptance">
				<p className="mb-3">
					An order you place is an offer to buy. A contract of sale is formed
					only when the Seller or WholesaleDai confirms the order.
				</p>
				<p className="mb-3">
					WholesaleDai or the Seller may reject or cancel an order before
					fulfilment where permitted by law, for reasons including product
					unavailability, pricing errors, suspected fraud, payment failure,
					verification issues or technical errors.
				</p>
			</SubSection>

			<SubSection heading="7.3 Electronic contracts">
				<p>
					You agree that orders, confirmations, acceptances and notices made
					electronically through the Platform are legally valid and enforceable
					electronic records under the Electronic Transactions Act, 2063 (2008),
					and that clicking "I agree", confirming an order or entering an OTP
					constitutes your signature and consent.
				</p>
			</SubSection>
		</Section>

		<Section heading="8. Payments">
			<SubSection heading="8.1 Available payment methods">
				<p>
					The Platform may support digital wallets, bank transfers, Cash on
					Delivery (COD) where available, and third-party payment gateways.
					Available methods may vary by transaction.
				</p>
			</SubSection>

			<SubSection heading="8.2 Payment service providers">
				<p>
					Electronic payments are processed through third-party payment service
					providers licensed by Nepal Rastra Bank. You may be required to accept
					the terms and conditions of the applicable payment provider.
				</p>
			</SubSection>

			<SubSection heading="8.3 Payment security">
				<p>
					Digital payment transactions are encrypted and processed through
					authorised payment service providers. WholesaleDai does not guarantee
					that external payment systems will always operate without interruption
					or error.
				</p>
			</SubSection>
		</Section>

		<Section heading="9. Delivery and shipping">
			<SubSection heading="9.1 Logistics">
				<p>
					Delivery may be performed by WholesaleDai, third-party logistics
					partners, Sellers or other authorised delivery providers, depending on
					the transaction.
				</p>
			</SubSection>

			<SubSection heading="9.2 Delivery estimates">
				<p className="mb-3">
					Delivery dates and schedules shown on the Platform are estimates
					unless expressly stated otherwise. WholesaleDai is not responsible for
					delays caused by circumstances beyond reasonable control, including
					natural disasters, strikes, road blockades, bandhs, political
					disturbances, government restrictions, transportation disruptions,
					supply chain disruptions and other unforeseen events.
				</p>
			</SubSection>

			<SubSection heading="9.3 Delivery information">
				<p>
					Buyers are responsible for providing accurate delivery information.
					WholesaleDai, Sellers and delivery partners are not responsible for
					delays or losses resulting from an inaccurate address or contact
					details provided by the Buyer.
				</p>
			</SubSection>

			<SubSection heading="9.4 Title and risk">
				<p>
					Unless otherwise agreed between the parties, risk of loss or damage
					passes to the Buyer on physical receipt of the goods at the designated
					delivery address. Title to the goods passes to the Buyer on receipt of
					full payment for those goods, or on delivery, whichever is later.
				</p>
			</SubSection>
		</Section>

		<Section heading="10. Cancellations">
			<p className="mb-3">
				Orders may be cancelled through the Platform's cancellation workflow.
				Cancellation is generally possible before Seller confirmation or before
				dispatch.
			</p>
			<p>
				Once an order has been confirmed, prepared, dispatched or otherwise
				processed, cancellation may not be available. Specific cancellation terms
				may depend on the Seller and the transaction.
			</p>
		</Section>

		<Section heading="11. Returns and refunds">
			<SubSection heading="11.1 Eligible returns">
				<p className="mb-3">Returns are accepted for:</p>
				<Bullets
					items={[
						"Damaged products",
						"Incorrect products, or products that do not match the listing description",
						"Incorrect quantities",
						"Defective or expired goods",
						"Other situations approved by the Seller or WholesaleDai",
					]}
				/>
			</SubSection>

			<SubSection heading="11.2 Reporting period">
				<p className="mb-3">
					Buyers should report an eligible issue within{" "}
					<span className="font-semibold">7 days of delivery</span>. Supporting
					evidence may be required, including photographs, videos, order details
					and delivery information.
				</p>
			</SubSection>

			<SubSection heading="11.3 Bulk sales">
				<p>
					Because of the commercial and wholesale nature of transactions on the
					Platform, "change of mind" returns are not generally accepted. Returns
					for change of preference, an incorrect purchasing decision or excess
					stock may be refused unless specifically authorised by the Seller or
					WholesaleDai.
				</p>
			</SubSection>

			<SubSection heading="11.4 Refund processing">
				<p className="mb-3">
					Where a return is accepted and a replacement is not provided, the
					refund covers the full amount paid for the goods, including taxes.
					Approved refunds are processed through the original payment method, or
					another method agreed under the transaction terms, within the agreed
					timeframe.
				</p>
				<p>
					Refund processing time may vary depending on the payment provider,
					bank, digital wallet, Seller and transaction verification.
				</p>
			</SubSection>

			<Note>
				<p>
					Nothing in this section limits any right you have that cannot be
					excluded by law, including rights under the Electronic Commerce Act,
					2081 (2025) to return goods that do not match the Seller's
					description, and rights under the Consumer Protection Act, 2075 (2018)
					where that Act applies to your purchase.
				</p>
			</Note>
		</Section>

		<Section heading="12. Prohibited conduct">
			<p className="mb-3">You agree not to:</p>
			<Bullets
				items={[
					"Violate applicable local, national or international laws, or trade regulations",
					"Provide false or misleading information, or create fraudulent accounts",
					"Post fraudulent or misleading product listings",
					"Sell illegal or counterfeit products",
					"Post defamatory or unlawful content",
					"Attempt to defraud Buyers, Sellers or WholesaleDai",
					"Circumvent payment systems or avoid legitimate Platform service charges",
					"Solicit off-platform transactions to circumvent Platform procedures",
					"Reverse engineer or decompile the Platform",
					"Scrape, copy or resell Platform data or content without our written authorisation",
					"Introduce malicious software, viruses or malware, or interfere with Platform security",
					"Attempt unauthorised access to accounts or data that are not yours",
					"Infringe intellectual property rights, or misuse another user's information",
				]}
			/>
			<p className="mt-3">
				Violation may result in suspension or termination of your account and
				possible legal action.
			</p>
		</Section>

		<Section heading="13. Intellectual property">
			<p className="mb-3">
				All intellectual property relating to WholesaleDai is owned by or
				licensed to WholesaleDai, including software, source code, application
				interfaces, website and visual design, branding, trademarks, logos, text,
				graphics, content and Platform features.
			</p>
			<p className="mb-3">
				You are granted a limited, non-exclusive, non-transferable, revocable
				licence to access and use the Platform solely for lawful business
				purposes.
			</p>
			<p>
				You may not copy, reproduce, modify, distribute, sell or exploit
				WholesaleDai intellectual property without our prior written
				authorisation. Sellers retain ownership of the content they upload and
				grant WholesaleDai a licence to display and promote it on the Platform.
			</p>
		</Section>

		<Section heading="14. Third-party products and services">
			<p>
				The Platform may contain products, services, information or content
				provided by third parties. WholesaleDai does not necessarily endorse
				third-party products or businesses. Where WholesaleDai acts solely as a
				marketplace facilitator, responsibility for third-party products remains
				with the relevant Seller, subject to applicable law.
			</p>
		</Section>

		<Section heading="15. Disclaimer of warranties">
			<p className="mb-3">
				The Platform is provided on an "as is" and "as available" basis. To the
				maximum extent permitted by applicable law, WholesaleDai does not
				guarantee that the Platform will be uninterrupted, error-free, completely
				secure, always available or free from technical problems.
			</p>
			<p>
				We may update, modify, suspend or discontinue Platform features where
				reasonably necessary.
			</p>
		</Section>

		<Section heading="16. Intermediary status">
			<p className="mb-3">
				WholesaleDai operates primarily as a B2B marketplace facilitator
				connecting Buyers and Sellers. While we conduct business verification and
				take reasonable measures to promote a reliable marketplace, we cannot
				guarantee the performance of every third-party Seller or Buyer.
			</p>
			<p className="mb-3">
				Unless goods are expressly sold or fulfilled directly by WholesaleDai, we
				are generally not responsible for product quality defects, Seller
				non-performance, Buyer non-payment, product misrepresentation by third
				parties, or commercial disputes between Buyers and Sellers.
			</p>
			<p>
				Nothing in these Terms limits the duties an intermediary owes under the
				Electronic Commerce Act, 2081 (2025), or any other responsibility that
				cannot legally be excluded.
			</p>
		</Section>

		<Section heading="17. Limitation of liability">
			<p className="mb-3">
				To the maximum extent permitted by applicable law, WholesaleDai is not
				liable for indirect, incidental or consequential damages, loss of
				profits, loss of business opportunities, loss of revenue, loss of data or
				business interruption.
			</p>
			<p className="mb-3">
				Where WholesaleDai is found liable, our total aggregate liability arising
				out of or relating to a transaction is limited to the amount paid for the
				order giving rise to the claim.
			</p>
			<p>
				Nothing in these Terms excludes or limits liability where such exclusion
				or limitation is prohibited by law, including liability for fraud or for
				death or personal injury caused by negligence.
			</p>
		</Section>

		<Section heading="18. Indemnification">
			<p className="mb-3">
				You agree to indemnify and hold harmless WholesaleDai, its employees,
				representatives, affiliates and service providers from claims, losses,
				liabilities, damages and expenses arising from:
			</p>
			<Bullets
				items={[
					"Your violation of these Terms",
					"Unlawful use of the Platform",
					"Fraudulent activity",
					"Violation of applicable laws",
					"Infringement of third-party rights",
					"Products or content you submit",
				]}
			/>
		</Section>

		<Section heading="19. Privacy">
			<p>
				Your use of WholesaleDai is also governed by our{" "}
				<Link to="/privacy" className="text-brand-600 font-semibold underline">
					Privacy Policy
				</Link>
				, which explains how we collect, use, store, process and protect personal
				and business information. By using the Platform you acknowledge and agree
				to that Policy.
			</p>
		</Section>

		<Section heading="20. Complaints and grievance redressal">
			<p className="mb-3">
				If you have a complaint about an order, a Seller, a Buyer or the
				Platform, contact us using the details in section 25, through the{" "}
				<Link
					to="/contact-us"
					className="text-brand-600 font-semibold underline"
				>
					contact page
				</Link>
				, or through the live chat on the Platform. Complaints may be submitted
				electronically, in writing or in person at our office.
			</p>
			<p>
				As required by the Electronic Commerce Act, 2081 (2025), we register
				every complaint on receipt, acknowledge it to the complainant,
				investigate it and aim to resolve it within 15 days.
			</p>
		</Section>

		<Section heading="21. Governing law">
			<p>
				These Terms and Conditions are governed by and construed in accordance
				with the laws of <span className="font-semibold">Nepal</span>.
			</p>
		</Section>

		<Section heading="22. Dispute resolution">
			<p className="mb-3">
				If a dispute arises out of or relating to these Terms or your use of the
				Platform, the parties should first attempt to resolve it through
				good-faith negotiation, using the complaints process in section 20.
			</p>
			<p>
				If the dispute cannot be resolved through negotiation, it shall be
				submitted to the competent courts having jurisdiction in{" "}
				<span className="font-semibold">Kathmandu, Nepal</span>. This does not
				remove your right to bring a claim before any forum or authority that has
				jurisdiction under Nepali law.
			</p>
		</Section>

		<Section heading="23. Modifications to these Terms">
			<p className="mb-3">
				WholesaleDai may update, modify or revise these Terms at any time.
				Updated Terms will be posted within the WholesaleDai application and on
				our website at{" "}
				<a
					href="https://www.wholesaledai.com"
					className="text-brand-600 font-semibold underline"
				>
					www.wholesaledai.com
				</a>
				, with the date at the top of this page updated.
			</p>
			<p>
				Where a change is significant we will notify you in the app or by email
				before it takes effect. Continued use of the Platform after updated Terms
				become effective constitutes acceptance of the revised Terms. We
				encourage you to review these Terms periodically.
			</p>
		</Section>

		<Section heading="24. Termination">
			<p className="mb-3">
				WholesaleDai may suspend or terminate access to the Platform where
				necessary, including where a user violates these Terms, engages in
				fraudulent activity, provides false information, violates applicable
				laws, creates security risks or misuses the Platform.
			</p>
			<p>
				You may close your own account at any time — see{" "}
				<Link
					to="/delete-account"
					className="text-brand-600 font-semibold underline"
				>
					deleting your account
				</Link>
				. Termination does not affect obligations, rights or liabilities that
				arose before it, including payment for orders already placed.
			</p>
		</Section>

		<Section heading="25. Contact information">
			<p className="mb-3">
				If you have questions, concerns, complaints or notices regarding these
				Terms and Conditions, please contact us:
			</p>
			<Bullets
				items={[
					"Company name: WholesaleDai",
					"Office address: O2, Newroad, Kathmandu-22, Nepal",
					"Phone: +977-1-5927066",
					"Email: info@wholesaledai.com",
					"Alternative email: infowholesaledai@gmail.com",
					"Website: www.wholesaledai.com",
				]}
			/>
		</Section>
	</LegalPage>
);

export default Terms;
